/**
 * WebContainer integration — entry point for the Studio's "complex build"
 * runtime (multi-file projects with real npm install, Vite, etc.).
 *
 * Why this module exists
 * ----------------------
 * The simple HTML preview (<iframe srcDoc>) is still the default for landing
 * pages and single-file apps. WebContainer is the opt-in runtime that kicks
 * in only when Claude produces a multi-file project (FileSystemTree format).
 *
 * Architecture
 * ------------
 * 1. `configureAPIKey(...)` is called ONCE at import-time with the commercial
 *    key from `frontend/.env` (`REACT_APP_STACKBLITZ_WC_API_KEY`). The key is
 *    pinned to the domains we declared in the StackBlitz dashboard, so
 *    exposing it in the JS bundle is by design (per WebContainer docs).
 * 2. `getContainer()` lazily boots a single WebContainer instance on first
 *    call. Booting is expensive (~5s) so we keep one instance for the whole
 *    session, just like bolt.new / lovable do.
 * 3. `coep` strategy: we use `'none'` on Emergent's preview domain because
 *    Emergent's Express proxy doesn't currently serve COEP/COOP headers.
 *    `'none'` works on Chromium-based browsers (Chrome, Edge, Brave, Opera).
 *    When Emergent ships proper headers, switch to `'credentialless'` to
 *    unlock Safari + Firefox.
 *
 * Public API
 * ----------
 *   - getContainer(): Promise<WebContainer>
 *   - isWebContainerSupported(): boolean
 *   - tearDownContainer(): void
 */

import { WebContainer, configureAPIKey } from '@webcontainer/api';

const API_KEY = process.env.REACT_APP_STACKBLITZ_WC_API_KEY || '';

// One-shot setup — must run BEFORE any WebContainer.boot() call.
// Per docs, calling configureAPIKey() after boot() throws.
let keyConfigured = false;
function ensureKeyConfigured() {
  if (keyConfigured) return;
  if (!API_KEY) {
    console.warn(
      '[WebContainer] REACT_APP_STACKBLITZ_WC_API_KEY is not set — falling back to OSS tier (limited usage).',
    );
  } else {
    try {
      configureAPIKey(API_KEY);
    } catch (err) {
      // `configureAPIKey` throws only if boot already ran. In practice we
      // never call it twice because of `keyConfigured`, but be defensive.
      console.warn('[WebContainer] configureAPIKey failed:', err?.message || err);
    }
  }
  keyConfigured = true;
}

// Single-instance promise. WebContainers can only run ONE instance per tab
// (per official docs) — booting a second one without `teardown()` first
// throws. We cache the boot promise so concurrent callers share it.
let bootPromise = null;

/**
 * Lazily boot (or return) the singleton WebContainer instance.
 * The first call takes ~5s (WASM load + Node.js init). Subsequent calls
 * resolve instantly.
 */
export async function getContainer() {
  if (typeof window === 'undefined') {
    throw new Error('WebContainer can only run in the browser');
  }
  if (bootPromise) return bootPromise;

  ensureKeyConfigured();

  bootPromise = WebContainer.boot({
    // 'credentialless' lets us run WebContainer without requiring every
    // cross-origin asset (Tailwind CDN, Font Awesome, picsum, Google Fonts)
    // to opt into CORP. coi-serviceworker (loaded in public/index.html)
    // provides the matching COOP+COEP headers client-side, so the page is
    // cross-origin isolated even when the Emergent ingress doesn't set
    // those headers itself.
    coep: 'credentialless',
    workdirName: 'studio',
    forwardPreviewErrors: 'exceptions-only',
  }).catch((err) => {
    // Reset on failure so the next attempt can retry from a clean state.
    bootPromise = null;
    throw err;
  });

  return bootPromise;
}

/**
 * Quick capability check. Useful for the UI to decide between the iframe
 * preview and the WebContainer preview without trying to boot.
 *
 * Chromium-only because we use `coep: 'none'`. When Emergent adds the
 * cross-origin headers we can drop this check.
 */
export function isWebContainerSupported() {
  if (typeof window === 'undefined') return false;
  // With coi-serviceworker injecting COOP+COEP, the page is cross-origin
  // isolated on the second load. Before the SW reload, crossOriginIsolated
  // is false — we still allow the UI to attempt boot so the SW can claim
  // the page and trigger its reload, after which WC works.
  const ua = navigator.userAgent || '';
  // credentialless is Chromium-only (Chrome / Edge / Brave / Opera).
  return Boolean(window.chrome) || /Chromium|Chrome|Edg|Brave|OPR/i.test(ua);
}

function coepMode() {
  return 'credentialless';
}

/**
 * Hard reset. Releases the current WebContainer so a fresh one can boot.
 * Use when the user opens a brand new project or to recover from a fatal
 * runtime error.
 */
export async function tearDownContainer() {
  if (!bootPromise) return;
  try {
    const wc = await bootPromise;
    wc.teardown();
  } catch {
    // If boot never completed, nothing to teardown.
  } finally {
    bootPromise = null;
  }
}
